import {
  type MarketplaceCategory,
  normalizeListingTag,
} from "@loot-os/fulfillment-core/listing/types";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@loot-os/ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { ChevronsUpDown, XIcon } from "lucide-react";
import { useState } from "react";

/**
 * The tags a StockX promotion usually wants; anything else is typed in. Every
 * member but LORCANA is a `MarketplaceCategory`, and `satisfies` keeps it that
 * way: the markup rule matches these strings exactly, so a rename over there has
 * to fail the build here rather than quietly stop matching.
 */
const SUGGESTED_TAGS = [
  "POKEMON",
  "ONE_PIECE",
  "RIFTBOUND",
  "MTG",
  "LORCANA",
  "SNEAKERS",
] as const satisfies readonly (MarketplaceCategory | "LORCANA")[];

/**
 * Picks the tags for a listing: the suggested set plus any tag the operator types.
 *
 * Typed tags are normalized as they are added, so the chip reads exactly what is
 * stored and an exact-match rule (markup, search aliases) still hits. cmdk's own
 * filtering is off because a typed "one piece" no longer matches the ONE_PIECE it
 * becomes.
 */
export function ListingTagsPicker({
  id,
  value,
  disabled,
  onChange,
}: {
  /** Ties the trigger to the field's `<Label htmlFor>`. */
  id: string;
  value: string[];
  disabled?: boolean;
  onChange: (tags: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const typed = normalizeListingTag(query);
  const options = SUGGESTED_TAGS.filter((tag) => !value.includes(tag) && tag.includes(typed));
  const isTypedNew =
    typed.length > 0 && !value.includes(typed) && !options.some((tag) => tag === typed);

  const addTag = (tag: string) => {
    onChange([...value, tag]);
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              className="h-8 w-full justify-between font-normal"
            />
          }
        >
          <span className="truncate text-muted-foreground">Choose or type a tag…</span>
          <ChevronsUpDown className="size-3.5 opacity-60" />
        </PopoverTrigger>
        <PopoverContent className="w-[22rem] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="POKEMON, ONE_PIECE…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>Type a tag to add it.</CommandEmpty>
              {options.map((tag) => (
                <CommandItem key={tag} value={tag} onSelect={() => addTag(tag)}>
                  {tag}
                </CommandItem>
              ))}
              {isTypedNew && (
                <CommandItem value={typed} onSelect={() => addTag(typed)}>
                  Add <span className="font-mono">{typed}</span>
                </CommandItem>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <Badge key={tag} variant="secondary" className="pr-1 font-mono">
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                disabled={disabled}
                onClick={() => onChange(value.filter((other) => other !== tag))}
                className="rounded-full opacity-60 hover:opacity-100"
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
